export interface HafezPoem {
  id: number;
  title: string;
  persianTitle: string;
  ghazalNumber: number;
  lines: PoemLine[];
  theme: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  culturalNotes: string;
  audioUrl?: string;
}

export interface PoemLine {
  id: number;
  persian: string;
  transliteration: string;
  english: string;
  wordByWord: WordTranslation[];
  audioUrl?: string;
}

export interface WordTranslation {
  persian: string;
  transliteration: string;
  english: string;
  partOfSpeech: string;
}

export interface HafezLesson {
  _id?: string;
  id: number;
  title: string;
  persianTitle: string;
  description: string;
  focus: string;
  goal: string;
  icon: string;
  poems: HafezPoem[];
  quiz: HafezQuizQuestion[];
}

export interface HafezQuizQuestion {
  id: number;
  question: string;
  type: 'multiple-choice' | 'translation' | 'fill-blank';
  options?: string[];
  correctAnswer: string;
  explanation: string;
  audioUrl?: string;
}

export const hafezLessonsData: HafezLesson[] = [
  {
    id: 1,
    title: "Introduction to Hafez",
    persianTitle: "مقدمه‌ای بر حافظ",
    description: "Learn the basics of reading and understanding Hafez's poetry",
    focus: "Basic ghazal structure and common themes",
    goal: "Understand the structure and beauty of Hafez's poetry",
    icon: "📖",
    poems: [
      {
        id: 1,
        title: "Ghazal 1 - The Beloved's Beauty",
        persianTitle: "غزل اول - زیبایی معشوق",
        ghazalNumber: 1,
        theme: "Love and Beauty",
        difficulty: "beginner",
        culturalNotes: "This ghazal introduces the central theme of divine love through the metaphor of earthly beauty. The beloved represents both human beauty and divine perfection.",
        lines: [
          {
            id: 1,
            persian: "الا یا ایها الساقی ادر کأسا و ناولها",
            transliteration: "Alā yā ayyuha-s-sāqī adir ka'san wa nāwil-hā",
            english: "Come, O cupbearer, pass around the cup and give it to us",
            wordByWord: [
              { persian: "الا", transliteration: "alā", english: "come", partOfSpeech: "interjection" },
              { persian: "یا", transliteration: "yā", english: "O", partOfSpeech: "vocative particle" },
              { persian: "ایها", transliteration: "ayyuha", english: "O", partOfSpeech: "vocative particle" },
              { persian: "الساقی", transliteration: "as-sāqī", english: "cupbearer", partOfSpeech: "noun" },
              { persian: "ادر", transliteration: "adir", english: "pass around", partOfSpeech: "verb" },
              { persian: "کأسا", transliteration: "ka'san", english: "cup", partOfSpeech: "noun" },
              { persian: "و", transliteration: "wa", english: "and", partOfSpeech: "conjunction" },
              { persian: "ناولها", transliteration: "nāwil-hā", english: "give it to us", partOfSpeech: "verb" }
            ],
            audioUrl: "/audio/come-o-cupbearer-pass-around-the-cup-and-give-it-to-us.wav"
          },
          {
            id: 2,
            persian: "که عشق آسان نمود اول ولی افتاد مشکل‌ها",
            transliteration: "Ki 'ishq āsān namūd avval valī uftād mushkil-hā",
            english: "For love seemed easy at first, but difficulties arose",
            wordByWord: [
              { persian: "که", transliteration: "ki", english: "for/because", partOfSpeech: "conjunction" },
              { persian: "عشق", transliteration: "'ishq", english: "love", partOfSpeech: "noun" },
              { persian: "آسان", transliteration: "āsān", english: "easy", partOfSpeech: "adjective" },
              { persian: "نمود", transliteration: "namūd", english: "seemed", partOfSpeech: "verb" },
              { persian: "اول", transliteration: "avval", english: "first", partOfSpeech: "adverb" },
              { persian: "ولی", transliteration: "valī", english: "but", partOfSpeech: "conjunction" },
              { persian: "افتاد", transliteration: "uftād", english: "fell/arose", partOfSpeech: "verb" },
              { persian: "مشکل‌ها", transliteration: "mushkil-hā", english: "difficulties", partOfSpeech: "noun" }
            ],
            audioUrl: "/audio/for-love-seemed-easy-at-first-but-difficulties-arose.wav"
          },
          {
            id: 3,
            persian: "به بوی نافهای کاخر صبا زان طره بگشاید",
            transliteration: "Bi būy-e nāfahā-yi kākhar sabā zān turah bagushāyad",
            english: "With the scent of the last breath, the morning breeze will open that lock of hair",
            wordByWord: [
              { persian: "به", transliteration: "bi", english: "with", partOfSpeech: "preposition" },
              { persian: "بوی", transliteration: "būy", english: "scent", partOfSpeech: "noun" },
              { persian: "نافهای", transliteration: "nāfahā", english: "breath", partOfSpeech: "noun" },
              { persian: "کاخر", transliteration: "kākhar", english: "last", partOfSpeech: "adjective" },
              { persian: "صبا", transliteration: "sabā", english: "morning breeze", partOfSpeech: "noun" },
              { persian: "زان", transliteration: "zān", english: "that", partOfSpeech: "demonstrative" },
              { persian: "طره", transliteration: "turah", english: "lock of hair", partOfSpeech: "noun" },
              { persian: "بگشاید", transliteration: "bagushāyad", english: "will open", partOfSpeech: "verb" }
            ],
            audioUrl: "/audio/with-the-scent-of-the-last-breath-the-morning-breeze-will-open-that-lock-of-hair.wav"
          }
        ]
      },
      {
        id: 2,
        title: "Ghazal 2 - The Wine of Love",
        persianTitle: "غزل دوم - شراب عشق",
        ghazalNumber: 2,
        theme: "Divine Love and Wine",
        difficulty: "beginner",
        culturalNotes: "This ghazal uses wine as a metaphor for divine love and spiritual intoxication. The tavern represents a place of spiritual gathering.",
        lines: [
          {
            id: 1,
            persian: "بیا و کشتی ما در شط شراب انداز",
            transliteration: "Biyā wa kashti-yi mā dar shatt-e sharāb andāz",
            english: "Come and cast our ship into the river of wine",
            wordByWord: [
              { persian: "بیا", transliteration: "biyā", english: "come", partOfSpeech: "verb" },
              { persian: "و", transliteration: "wa", english: "and", partOfSpeech: "conjunction" },
              { persian: "کشتی", transliteration: "kashti", english: "ship", partOfSpeech: "noun" },
              { persian: "ما", transliteration: "mā", english: "our", partOfSpeech: "pronoun" },
              { persian: "در", transliteration: "dar", english: "into", partOfSpeech: "preposition" },
              { persian: "شط", transliteration: "shatt", english: "river", partOfSpeech: "noun" },
              { persian: "شراب", transliteration: "sharāb", english: "wine", partOfSpeech: "noun" },
              { persian: "انداز", transliteration: "andāz", english: "cast", partOfSpeech: "verb" }
            ],
            audioUrl: "/audio/come-and-cast-our-ship-into-the-river-of-wine.wav"
          },
          {
            id: 2,
            persian: "خروش و ولوله در جان شیخ و شاب انداز",
            transliteration: "Kharūsh wa walwalah dar jān-e shaykh wa shāb andāz",
            english: "Create uproar and commotion in the soul of the sheikh and the youth",
            wordByWord: [
              { persian: "خروش", transliteration: "kharūsh", english: "uproar", partOfSpeech: "noun" },
              { persian: "و", transliteration: "wa", english: "and", partOfSpeech: "conjunction" },
              { persian: "ولوله", transliteration: "walwalah", english: "commotion", partOfSpeech: "noun" },
              { persian: "در", transliteration: "dar", english: "in", partOfSpeech: "preposition" },
              { persian: "جان", transliteration: "jān", english: "soul", partOfSpeech: "noun" },
              { persian: "شیخ", transliteration: "shaykh", english: "sheikh", partOfSpeech: "noun" },
              { persian: "و", transliteration: "wa", english: "and", partOfSpeech: "conjunction" },
              { persian: "شاب", transliteration: "shāb", english: "youth", partOfSpeech: "noun" },
              { persian: "انداز", transliteration: "andāz", english: "create", partOfSpeech: "verb" }
            ],
            audioUrl: "/audio/create-uproar-and-commotion-in-the-soul-of-the-sheikh-and-the-youth.wav"
          }
        ]
      }
    ],
    quiz: [
      {
        id: 1,
        question: "What does 'ساقی' (sāqī) mean in Hafez's poetry?",
        type: "multiple-choice",
        options: [
          "A teacher",
          "A cupbearer",
          "A friend",
          "A lover"
        ],
        correctAnswer: "A cupbearer",
        explanation: "ساقی (sāqī) means 'cupbearer' and is a central figure in Hafez's poetry, representing both the earthly wine server and the divine giver of spiritual wine.",
        audioUrl: "/audio/what-does-sāqī-mean-in-hafez-s-poetry.wav"
      },
      {
        id: 2,
        question: "What is the main theme of Ghazal 1?",
        type: "multiple-choice",
        options: [
          "Nature and seasons",
          "Love and beauty",
          "War and peace",
          "Travel and journey"
        ],
        correctAnswer: "Love and beauty",
        explanation: "Ghazal 1 focuses on love and beauty, using the metaphor of the beloved's beauty to represent divine perfection.",
        audioUrl: "/audio/what-is-the-main-theme-of-ghazal-1.wav"
      },
      {
        id: 3,
        question: "Translate: 'عشق آسان نمود اول'",
        type: "translation",
        correctAnswer: "Love seemed easy at first",
        explanation: "This line describes how love appears simple initially, but becomes complex as one delves deeper into its mysteries.",
        audioUrl: "/audio/translate-ishq-āsān-namūd-avval.wav"
      }
    ]
  },
  {
    id: 2,
    title: "Advanced Themes in Hafez",
    persianTitle: "مضامین پیشرفته در حافظ",
    description: "Explore deeper themes and complex metaphors in Hafez's poetry",
    focus: "Complex metaphors and spiritual symbolism",
    goal: "Understand the deeper layers of meaning in Hafez's work",
    icon: "🌟",
    poems: [
      {
        id: 3,
        title: "Ghazal 3 - The Mystic's Journey",
        persianTitle: "غزل سوم - سفر عارفانه",
        ghazalNumber: 3,
        theme: "Mysticism and Spiritual Journey",
        difficulty: "intermediate",
        culturalNotes: "This ghazal explores the Sufi concept of spiritual journey and the search for divine truth. The tavern represents a place of spiritual awakening.",
        lines: [
          {
            id: 1,
            persian: "دوش وقت سحر از غصه نجاتم دادند",
            transliteration: "Dūsh waqt-e sahar az ghusseh najātam dādand",
            english: "Last night at dawn, they delivered me from sorrow",
            wordByWord: [
              { persian: "دوش", transliteration: "dūsh", english: "last night", partOfSpeech: "adverb" },
              { persian: "وقت", transliteration: "waqt", english: "time", partOfSpeech: "noun" },
              { persian: "سحر", transliteration: "sahar", english: "dawn", partOfSpeech: "noun" },
              { persian: "از", transliteration: "az", english: "from", partOfSpeech: "preposition" },
              { persian: "غصه", transliteration: "ghusseh", english: "sorrow", partOfSpeech: "noun" },
              { persian: "نجاتم", transliteration: "najātam", english: "my deliverance", partOfSpeech: "noun" },
              { persian: "دادند", transliteration: "dādand", english: "they gave", partOfSpeech: "verb" }
            ],
            audioUrl: "/audio/last-night-at-dawn-they-delivered-me-from-sorrow.wav"
          },
          {
            id: 2,
            persian: "واندر آن ظلمت شب آب حیاتم دادند",
            transliteration: "W-andar ān zulmat-e shab āb-e hayātam dādand",
            english: "And in that darkness of night, they gave me the water of life",
            wordByWord: [
              { persian: "واندر", transliteration: "w-andar", english: "and in", partOfSpeech: "preposition" },
              { persian: "آن", transliteration: "ān", english: "that", partOfSpeech: "demonstrative" },
              { persian: "ظلمت", transliteration: "zulmat", english: "darkness", partOfSpeech: "noun" },
              { persian: "شب", transliteration: "shab", english: "night", partOfSpeech: "noun" },
              { persian: "آب", transliteration: "āb", english: "water", partOfSpeech: "noun" },
              { persian: "حیات", transliteration: "hayāt", english: "life", partOfSpeech: "noun" },
              { persian: "دادند", transliteration: "dādand", english: "they gave", partOfSpeech: "verb" }
            ],
            audioUrl: "/audio/and-in-that-darkness-of-night-they-gave-me-the-water-of-life.wav"
          }
        ]
      }
    ],
    quiz: [
      {
        id: 4,
        question: "What does 'آب حیات' (āb-e hayāt) symbolize in Sufi poetry?",
        type: "multiple-choice",
        options: [
          "Physical water",
          "Spiritual enlightenment",
          "Earthly wealth",
          "Temporal power"
        ],
        correctAnswer: "Spiritual enlightenment",
        explanation: "آب حیات (āb-e hayāt) symbolizes spiritual enlightenment and divine knowledge in Sufi poetry.",
        audioUrl: "/audio/what-does-āb-e-hayāt-symbolize-in-sufi-poetry.wav"
      },
      {
        id: 5,
        question: "What time of day is mentioned in Ghazal 3?",
        type: "multiple-choice",
        options: [
          "Noon",
          "Dawn",
          "Sunset",
          "Midnight"
        ],
        correctAnswer: "Dawn",
        explanation: "The poem mentions 'سحر' (sahar) which means dawn, a time often associated with spiritual awakening in Sufi tradition.",
        audioUrl: "/audio/what-time-of-day-is-mentioned-in-ghazal-3.wav"
      }
    ]
  }
];

export const getAllPoems = (): HafezPoem[] => {
  return hafezLessonsData.flatMap(lesson => lesson.poems);
};

export const getLessonById = (id: string | number): HafezLesson | undefined => {
  return hafezLessonsData.find(lesson =>
    lesson.id === id ||
    lesson.id === Number(id) ||
    (lesson._id && lesson._id === id)
  );
};

export const getPoemById = (id: number): HafezPoem | undefined => {
  return getAllPoems().find(poem => poem.id === id);
}; 